import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useDialog } from '../../../context/Dialog';
import useIonicToast from '../../../hooks/useIonicToast';
import walletIndividualKycSchema, {
  WalletIndividualKyc,
} from '../../../schemas/wallet-individual-kyc-schema';
import { RootState } from '../../../store/store';
import { WalletSetupResume } from '../../../types/wallet/Walletkyc';
import createWallet from '../walletCreation/createWallet';
import getWalletAddresses from '../walletCreation/getWalletAddresses';
import createIndividualProfile from '../kyc/createIndividualProfile';
import submitIndividualNIN from '../kyc/submitIndividualNIN';
import submitIndividualBVN from '../kyc/submitIndividualBVN';
import verifyIndividualAddress from '../kyc/verifyIndividualAddress';
import { extractApiErrorMessage } from '../../../utils/helperFunction/extractApiErrorMessage';
import { toYmd } from '../../../utils/helperFunction/toYmd';

// const isVerifiedStatus = (
//   status?: WalletKycDocVerificationStatus | WalletKycVerificationStatus | null
// ) => status?.toLowerCase() === 'verified';

// const PENDING_ITEM_MESSAGES: Partial<Record<PendingItems, string>> = {
//   provider_customer_creation: 'Your provider customer profile is still pending.',
//   kyc_profile: 'Your KYC profile is not complete yet.',
//   nin_verification: 'Your NIN verification is still pending.',
//   bvn_verification: 'Your BVN verification is still pending.',
//   address_verification: 'Your address verification is still pending.',
//   overall_kyc_verification: 'Your KYC is still under review.',
// };

// const getCustomerKycState = (customer: WalletKycCustomer | null) => {
//   if (!customer) return null;

//   return {
//     nin:
//       customer.kyc_status?.checks?.nin?.status || customer.kyc?.nin_status,
//     bvn:
//       customer.kyc_status?.checks?.bvn?.status || customer.kyc?.bvn_status,
//     address:
//       customer.kyc_status?.checks?.address?.status ||
//       customer.kyc?.address_status,
//     overall:
//       customer.kyc_status?.overall_status || customer.kyc?.overall_status,
//   };
// };

// const getInitialStep = (customer: WalletKycCustomer | null) => {
//   if (!customer) return 0;

//   const kyc = getCustomerKycState(customer);
//   if (!kyc) return 0;

//   if (isVerifiedStatus(kyc.overall)) return 5;
//   if (!isVerifiedStatus(kyc.nin)) return 1;
//   if (!isVerifiedStatus(kyc.bvn)) return 2;
//   if (!isVerifiedStatus(kyc.address)) return 3;

//   return 4;
// };

// const getPendingMessage = (pendingItems: string[] = []) => {
//   const firstMessage = pendingItems
//     .map((item) => PENDING_ITEM_MESSAGES[item])
//     .find(Boolean);
//   return firstMessage || 'Your KYC is still being reviewed.';
// };

type UseWalletIndividualKycProps = {
  setupResume?: WalletSetupResume | null;
};

export function useWalletIndividualKyc(props?: UseWalletIndividualKycProps) {
  const {
    userSession: { signedAccessToken },
    userStores: { hasStore },
    userProfile,
  } = useSelector((state: RootState) => ({
    userSession: state.userSession,
    userStores: state.userStores,
    userProfile: state.userProfile,
  }));
  const { closeDialog, setBusy } = useDialog();
  const { presentIonToast } = useIonicToast();
  const queryClient = useQueryClient();
  const invalidateWalletInfo = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['wallet'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['wallet-status'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['wallet-status', signedAccessToken],
      }),
      queryClient.invalidateQueries({
        queryKey: ['wallet-kyc-context', signedAccessToken],
      }),
    ]);
  /// STEPS
  const [step, setStep] = useState(props?.setupResume?.initial_step || 0);

  // FORM CONTEXT
  const methods = useForm<WalletIndividualKyc>({
    resolver: zodResolver(walletIndividualKycSchema),
  });

  // USER ADDRESS LIST
  const addressesQuery = useQuery({
    queryKey: ['wallet-addresses', signedAccessToken],
    queryFn: async (): Promise<
      Array<{ label: string; value: number; disabled: boolean }>
    > => {
      if (!signedAccessToken) return [];
      const addresses = await getWalletAddresses(signedAccessToken);

      return addresses.map(({ id, country, state, street, is_active }) => ({
        label: `${street} ${state}, ${country}`,
        value: id,
        disabled: !is_active,
      }));
    },
    enabled: Boolean(signedAccessToken),
  });

  // EXISTING ADDRESS LIST
  const addressList = addressesQuery.data || [];

  // AUTO FILL FORM
  useEffect(() => {
    methods.reset({
      customer_id: props?.setupResume?.customer_id || undefined, /// THIS IS USED IN PLACE OF CUSTOMER ID
      dob:
        toYmd(
          userProfile?.extended_user?.meta_data?.primary_information
            ?.date_of_birth
        ) || undefined,
      first_name: userProfile?.user?.first_name,
      last_name: userProfile?.user?.last_name,
      phone: userProfile?.user?.phone,
      addressChoice: 'new',
    });
  }, [props?.setupResume, userProfile]);

  /// LOADER STATE MANAGER
  const withBusyState = useCallback(
    async (callback: () => Promise<void>) => {
      try {
        setBusy(true);
        await callback();
      } finally {
        setBusy(false);
      }
    },
    [setBusy]
  );

  /// CREATE WALLET PROFILE
  const createProfile = useCallback(async () => {
    const values = methods.getValues();

    let triggerArr: Array<keyof WalletIndividualKyc> = ['phone', 'dob'];
    if (values.addressChoice == 'new') {
      triggerArr.push('newAddress');
    } else if (values.addressChoice == 'saved') {
      triggerArr.push('savedAddressId');
    }
    const isValid = await methods.trigger(triggerArr);

    if (!isValid || !signedAccessToken) return;

    const normalizedDob = toYmd(values.dob);
    if (!normalizedDob) {
      presentIonToast(
        'Please enter your date of birth in YYYY-MM-DD format.',
        'error'
      );
      return;
    }

    // Match the backend's Sterling sanitization so trailing dots/spaces don't
    // make it into the payload (Sterling rejects "...Lugbe. , Abuja, FCT" style
    // strings with "Invalid Address entered").
    const cleanAddrField = (s?: string) =>
      (s ?? '')
        .trim()
        .replace(/[.,;\s]+$/, '')
        .trim();
    const cleanedNewAddress = values.newAddress
      ? {
        ...values.newAddress,
        street: cleanAddrField(values.newAddress.street),
        street_line_2: cleanAddrField(values.newAddress.street_line_2),
        city: cleanAddrField(values.newAddress.city),
        state: cleanAddrField(values.newAddress.state.id),
        postal_code: cleanAddrField(values.newAddress.postal_code),
        country: cleanAddrField(values.newAddress.country.id),
        lga: cleanAddrField(values.newAddress.lga),
      }
      : values.newAddress;

    await withBusyState(async () => {
      try {
        const response = await createIndividualProfile(
          {
            phone: values.phone?.trim(),
            dob: normalizedDob,

            address_choice: {
              type: values.addressChoice as 'saved' | 'new',
              ...(values.addressChoice === 'saved'
                ? {
                  saved_address_id: Number(values.savedAddressId),
                  // Store-app's picker reads from /sterling/kyc/addresses,
                  // so the id is always a wallet_addresses row.
                  source: 'wallet' as const,
                }
                : { new_address: cleanedNewAddress }),
            },
          },
          signedAccessToken
        );

        if (response?.customer_id) {
          methods.setValue('customer_id', Number(response.customer_id));
        }

        invalidateWalletInfo();
        setStep(1);
        presentIonToast('Profile created');
      } catch (err: any) {
        presentIonToast(
          extractApiErrorMessage(err, 'Failed to create profile'),
          'error'
        );
      }
    });
  }, [methods, presentIonToast, signedAccessToken, withBusyState]);

  const submitSecurityInformation = useCallback(async () => {
    const isValid = await methods.trigger(['nin', 'bvn']);

    const customerId =
      methods.getValues('customer_id') || props?.setupResume?.customer_id;

    if (!isValid || !customerId || !signedAccessToken) return;
    const { bvn, nin } = methods.getValues();

    await withBusyState(async () => {
      try {
        await Promise.all([
          submitIndividualNIN(customerId, nin, signedAccessToken),
          submitIndividualBVN(customerId, bvn, signedAccessToken),
        ]);

        invalidateWalletInfo();
        setStep(2); // Step 2 = Create Wallet (address verification is Tier 3, not required)
        presentIonToast('NIN and BVN verified');
      } catch (err: any) {
        presentIonToast(
          extractApiErrorMessage(err, 'Failed to verify NIN/BVN'),
          'error'
        );
      }
    });
  }, [
    props?.setupResume?.customer_id,
    methods,
    presentIonToast,
    signedAccessToken,
    withBusyState,
  ]);

  // const submitBVN = useCallback(async () => {
  //   const isValid = await methods.trigger(['bvn']);
  //   const customerId =
  //     methods.getValues('customer_id') || props?.setupResume?.customer_id;

  //   if (!isValid || !customerId || !signedAccessToken) return;

  //   await withBusyState(async () => {
  //     try {
  //       await submitIndividualBVN(
  //         customerId,
  //         methods.getValues('bvn'),
  //         signedAccessToken
  //       );
  //       invalidateWalletInfo();
  //       setStep(3);
  //       presentIonToast('BVN verified');
  //     } catch (err: any) {
  //       presentIonToast(err.message, 'error');
  //     }
  //   });
  // }, [
  //   props?.setupResume?.customer_id,
  //   methods,
  //   presentIonToast,
  //   signedAccessToken,
  //   withBusyState,
  // ]);

  // VERIFY ADDRESS
  const verifyAddress = useCallback(async () => {
    const { savedAddressId, disco_code, meter_number } = methods.getValues();

    const triggerArr: Array<keyof WalletIndividualKyc> = savedAddressId?.trim()
      ? ['savedAddressId', 'disco_code', 'meter_number']
      : [];
    const isValid = await methods.trigger(triggerArr);

    if (!isValid || !signedAccessToken || !disco_code || !meter_number) return;
    if (!savedAddressId) {
      const message = 'Select an address to verify.';
      presentIonToast(message, 'error');
      return;
    }

    await withBusyState(async () => {
      try {
        await verifyIndividualAddress(
          {
            address_id: Number(savedAddressId),
            disco_code,
            meter_number,
          },
          signedAccessToken
        );
        presentIonToast('Address submitted for verification');
        invalidateWalletInfo();
        closeDialog();
      } catch (err: any) {
        presentIonToast(
          extractApiErrorMessage(err, 'Failed to submit address'),
          'error'
        );
      }
    });
  }, [closeDialog, methods, presentIonToast, signedAccessToken, withBusyState]);

  /// NEW WALLET CREATION REQUEST
  const submitWalletCreation = useCallback(async () => {
    const customerId =
      methods.getValues('customer_id') || props?.setupResume?.customer_id;

    if (!customerId || !signedAccessToken) return;

    await withBusyState(async () => {
      try {
        const response = await createWallet({
          customer_id: customerId,
          authToken: signedAccessToken,
        });

        if (!response?.success) {
          const message = 'We are unable to create your wallet, try again.';
          presentIonToast(message, 'error');
          return;
        }

        invalidateWalletInfo();
        presentIonToast('Wallet successfully created.');
        setStep(3); // Advance to optional Tier 3 address verification upgrade
      } catch (err: any) {
        presentIonToast(
          extractApiErrorMessage(err, 'Failed to create wallet'),
          'error'
        );
      }
    });
  }, [
    props?.setupResume?.customer_id,
    methods,
    presentIonToast,
    queryClient,
    signedAccessToken,
    withBusyState,
  ]);

  const next = useCallback(async () => {
    if (step === 0) {
      await createProfile();
      return;
    }

    if (step === 1) {
      await submitSecurityInformation();
      return;
    }
    // Step 2 is wallet creation — handled by form submit (handleFormSubmission)

    if (step === 3) {
      await verifyAddress();
      return;
    }
  }, [createProfile, step, submitSecurityInformation, verifyAddress]);

  const handleFormSubmission = async (e: FormEvent) => {
    e.preventDefault();
    if (step !== 2) return; // Step 2 = Create Wallet (Tier 2: NIN + BVN only)
    await submitWalletCreation();
  };

  const back = useCallback(() => {
    setStep((currentStep) => Math.max(0, currentStep - 1));
  }, []);

  const skip = useCallback(() => {
    if (step === 3) {
      closeDialog(); // Tier 3 upgrade is optional — close dialog when skipped
      return;
    }
    setStep((currentStep) => Math.min(3, currentStep + 1));
  }, [closeDialog, step]);

  return {
    methods,
    step,
    next,
    back,
    skip,
    addressList,
    hasStore,
    handleFormSubmission,
  };
}
